import React, { useState } from 'react';
import { Star, ChevronLeft, ChevronRight, ThumbsUp, Share2, CheckCircle2 } from 'lucide-react';
import { trustpilotReviews } from '../mock/mock';

const RatingStars = ({ rating }) => {
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <div key={i} className="relative">
          <Star className="w-4 h-4 text-white/10" fill="currentColor" />
          {rating >= i && <Star className="w-4 h-4 text-[#00b67a] absolute inset-0" fill="currentColor" />}
          {rating > i - 1 && rating < i && (
            <div className="absolute inset-0 overflow-hidden" style={{ width: `${(rating - (i-1)) * 100}%` }}>
              <Star className="w-4 h-4 text-[#00b67a]" fill="currentColor" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const Trustpilot = () => {
  const [start, setStart] = useState(0);
  const perPage = 4;
  const max = Math.max(0, trustpilotReviews.length - perPage);

  const visible = trustpilotReviews.slice(start, start + perPage);

  return (
    <section id="reviews" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="text-[#00b67a] text-xs font-bold uppercase tracking-widest mb-3">Trustpilot</div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Excellents avis, <span className="gradient-text">vérifiés.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <div className="glass-card rounded-2xl p-5 flex items-center gap-5">
              <div>
                <div className="text-white/60 text-xs mb-1">Note globale</div>
                <div className="flex items-center gap-2">
                  <span className="text-4xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>4.8</span>
                  <span className="text-white/50 text-sm">/ 5</span>
                </div>
                <RatingStars rating={4.8} />
                <div className="text-white/50 text-xs mt-1">Sur 12 340 avis</div>
              </div>
              <div className="h-16 w-px bg-white/10" />
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-[#00b67a] flex items-center justify-center">
                  <Star className="w-6 h-6 text-white" fill="white" />
                </div>
                <div>
                  <div className="text-white font-bold text-sm leading-tight">Trustpilot</div>
                  <div className="text-white/50 text-[10px]">Vérifié</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {visible.map((r, i) => (
            <div key={`${r.name}-${i}`} className="glass-card rounded-2xl p-5 flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                {r.avatar ? (
                  <img src={r.avatar} alt={r.name} className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center font-bold text-white">{r.name.charAt(0)}</div>
                )}
                <div className="flex-1">
                  <div className="text-white font-semibold text-sm">{r.name}</div>
                  <div className="text-white/40 text-xs">{r.date}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <RatingStars rating={r.rating} />
                <div className="flex items-center gap-1 text-[10px] text-[#00b67a] font-semibold">
                  <CheckCircle2 className="w-3 h-3" /> Vérifié
                </div>
              </div>
              <div className="text-white font-semibold text-sm mb-2">{r.title}</div>
              <p className="text-white/60 text-sm leading-relaxed flex-1">{r.text}</p>
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/5 text-white/40 text-xs">
                <button className="flex items-center gap-1 hover:text-white transition"><ThumbsUp className="w-3 h-3" /> Utile</button>
                <button className="flex items-center gap-1 hover:text-white transition"><Share2 className="w-3 h-3" /> Partager</button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-8">
          <div className="text-white/50 text-sm">Page {Math.floor(start/perPage)+1} sur {Math.ceil(trustpilotReviews.length/perPage)}</div>
          <div className="flex gap-2">
            <button onClick={() => setStart(Math.max(0, start - perPage))} disabled={start === 0}
              className="w-11 h-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10 transition disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => setStart(Math.min(max, start + perPage))} disabled={start >= max}
              className="w-11 h-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10 transition disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Trustpilot;
